<template>
  <div
    id="permissions"
    :class="{ 'permissions': true, 'is-loading': !loaded, 'pt-0': true, 'save-in-progress': orderSaveInProgress }">
    <fieldset class="mb-0">
      <legend>Permissions</legend>

      <d-alert
        v-if="referenceId === 0"
        theme="light"
        show
        class="text-center">
        You will be able to see and add permissions after saving this flight.
      </d-alert>

      <d-alert
        v-if="loadError"
        theme="warning"
        show
        class="text-center">
        An error occurred during loading permissions.
      </d-alert>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="mb-3">
        <d-col cols="3" class="pt-3">
          <span>Departure airport:</span><br>
          <a :href="baseUrl + '/dashboard/airports/' + departureAirportId" target="_blank" class="text-primary">
            {{ departureAirport }}
          </a>
        </d-col>

        <d-col cols="3" class="pt-3">
          <span>Arrival airport:</span><br>
          <a :href="baseUrl + '/dashboard/airports/' + arrivalAirportId" target="_blank" class="text-primary">
            {{ arrivalAirport }}
          </a>
        </d-col>

        <d-col cols="6" class="pt-3 d-flex">
          <d-button
            theme="primary"
            @click.prevent="sumUpIsVisible = true;"
            outline
            class="ml-auto">
            Field18 Text
          </d-button>

          <d-button
            theme="success"
            @click.prevent="addPermission"
            outline
            class="ml-4">
            Add permission
          </d-button>
        </d-col>
      </d-row>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="pl-3 pr-3">
        <table class="table">
          <thead>
            <tr>
              <th></th>
              <th>Start</th>
              <th>Country</th>
              <th>Entry point</th>
              <th>EET</th>
              <th>Number</th>
              <th>Validity</th>
              <th class="text-center">Warnings</th>
              <th class="text-center">Confirmed</th>
              <th class="text-center">Requested</th>
              <th>Actions</th>
            </tr>
          </thead>
          <draggable
            v-model="permissions"
            tag="tbody">
            <permissions-list-item
              v-for="(permissionData, index) of permissions"
              :key="'permissions-list-item-' + index"
              :referenceId="referenceId"
              :permissionData="permissionData" />
          </draggable>
        </table>
      </d-row>

      <d-alert
        v-if="loaded && !loadError && referenceId !== 0 && permissions.length === 0"
        theme="light"
        show
        class="text-center">
        There are no permissions for this flight.
      </d-alert>
    </fieldset>

    <d-modal
      v-if="sumUpIsVisible"
      size="lg"
      @close="sumUpIsVisible = false">
      <d-modal-header>
        <d-modal-title>
          Sum up permissions for flight {{ aircraftReginMark }}
          <span>
            {{ departureDate | displayDate }}
            <template v-if="departureAirportData">{{ departureAirportData.iata }}</template>
            {{ departureDate | displayHour }} - {{ arrivalDate | displayHour }}
            <template v-if="arrivalAirportData">{{ arrivalAirportData.iata }}</template>
            <template v-if="departureDate.split(' ')[0] !== arrivalDate.split(' ')[0]">
              {{ arrivalDate | displayDate }}
            </template>
          </span>
          <br>
          <small>
            {{ customerCompanyName }}
          </small>
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        {{ permissionsSummary }}
      </d-modal-body>
      <d-modal-footer>
        <d-button
          theme="info"
          class="ml-auto mr-auto"
          @click.prevent="copyToClipboard(permissionsSummary)">
          Copy
        </d-button>
      </d-modal-footer>
    </d-modal>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils.js';
import PermissionsListItem from './PermissionsListItem.vue';
import vueDraggable from 'vuedraggable';
import Vue from 'vue';

export default {
  name: 'permissions-list',
  components: {
    'permissions-list-item': PermissionsListItem,
    'draggable': vueDraggable
  },
  props: {
    aircraftReginMark: {
      type: String,
      required: true
    },
    arrivalAirportData: {
      type: Object,
      required: true
    },
    arrivalDate: {
      type: String,
      required: true
    },
    customerCompanyName: {
      type: String,
      required: false
    },
    departureAirportData: {
      type: Object,
      required: true
    },
    departureDate: {
      type: String,
      required: true
    },
    referenceId: {
      type: Number,
      required: true
    },
    departureAirport: {
      type: String,
      required: true
    },
    departureAirportId: {
      type: Number,
      required: true
    },
    arrivalAirport: {
      type: String,
      required: true
    },
    arrivalAirportId: {
      type: Number,
      required: true
    }
  },
  filters: {
    displayHour (value) {
      if (value) {
        value = value.split(' ')[1];
        value = value.split(':');
        value = [value[0], value[1]].join('');
        return value;
      }

      return '';
    },
    displayDate (value) {
      if (value) {
        let input = new Date(value);

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('');
      }

      return '';
    }
  },
  watch: {
    'permissions': function (newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        return;
      }

      if (this.permissions.length > 0) {
        this.debouncedSaveOfOrdering();
      }
    }
  },
  computed: {
    baseUrl () {
      return window.location.protocol + '//' + window.location.host;
    },
    permissionsSummary () {
      if (!this.permissions) {
        return '';
      }

      return this.permissions.map(permission => {
        let country = permission.country.field_18;
        let permissionNumber = permission.number.replace(/[^a-z0-9]/gmi, '');

        if (!country && permission.country.name) {
          country = permission.country.name.toLocaleUpperCase();
        }

        return [country, permissionNumber].join(' ');
      }).join(' ');
    }
  },
  data () {
    return {
      loaded: false,
      loadError: false,
      orderSaveInProgress: false,
      permissions: [],
      sumUpIsVisible: false
    };
  },
  mounted () {
    if (this.referenceId === 0) {
      this.loaded = true;
    } else {
      this.loadPermissions();
    }

    this.$bus.$on('permissions-list-reload', this.reload);
  },
  methods: {
    loadPermissions () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/trips/permissions/items',
        params: {
          pagination: 0,
          where: {
            flight_id: this.referenceId
          }
        },
        loadedKey: 'loaded',
        outputKey: 'permissions',
        errorKey: 'loadError',
        successAction: () => {
          this.permissions.sort((itemA, itemB) => itemA.ordering - itemB.ordering);
        }
      });
    },
    addPermission () {
      this.$bus.$emit('show-permission-popup', 'add', this.referenceId);
    },
    reload () {
      this.loaded = false;
      this.loadPermissions();
    },
    // eslint-disable-next-line
    debouncedSaveOfOrdering: debounce(function () {
      this.saveItemsOrder();
    }, 1000),
    saveItemsOrder () {
      this.orderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/trips/permissions/set-ordering',
        formData: {
          items: this.permissions.map(item => item.id),
          flight_id: this.referenceId
        },
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during ordering change. Please try again.',
        successAction: () => {
          this.orderSaveInProgress = false;
        }
      });
    },
    copyToClipboard (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.sumUpIsVisible = false;
      }).catch(() => {
        Vue.swal({
          title: 'Unable to copy',
          html: 'Cannot copy text to clipboard',
          icon: 'error',
          confirmButtonText: 'OK',
          buttonsStyling: true,
          timer: 2000,
          timerProgressBar: true
        });
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('permissions-list-reload', this.reload);
  }
}
</script>

<style lang="scss" scoped>
.permissions {
  margin: 30px 0;
  position: relative;

  &.save-in-progress,
  &.is-loading {
    fieldset {
      min-height: 120px;
    }

    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }

  &.save-in-progress {
    &:before {
      content: "Save in progress...";
    }
  }

  /deep/ .modal-dialog {
    margin: 100px auto 1.875rem auto!important;
  }
}
</style>
